import { TILE } from './index.js';
const dist2 = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2;
const grids = new Map();
const CLEAR = 60; // keke radius plus a margin
function segDist(p, a, b) {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const len2 = vx * vx + vy * vy;
    const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * vx + (p.y - a.y) * vy) / len2));
    return Math.hypot(p.x - a.x - t * vx, p.y - a.y - t * vy);
}
export function gridOf(map) {
    let g = grids.get(map.id);
    if (g)
        return g;
    const w = Math.ceil(map.width / TILE);
    const h = Math.ceil(map.height / TILE);
    const open = new Uint8Array(w * h);
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
            const c = centre(x, y);
            open[y * w + x] = map.walls.every((s) => segDist(c, s.a, s.b) >= CLEAR) ? 1 : 0;
        }
    }
    g = { w, h, open };
    grids.set(map.id, g);
    return g;
}
const centre = (x, y) => ({ x: x * TILE + TILE / 2, y: y * TILE + TILE / 2 });
/** The nearest open cell to a point (a station can sit close to a wall). */
export function nearestOpen(g, p) {
    const cx = Math.floor(p.x / TILE);
    const cy = Math.floor(p.y / TILE);
    for (let r = 0; r <= 3; r++) {
        let best = null;
        let bestD = Infinity;
        for (let y = cy - r; y <= cy + r; y++) {
            for (let x = cx - r; x <= cx + r; x++) {
                if (x < 0 || y < 0 || x >= g.w || y >= g.h || !g.open[y * g.w + x])
                    continue;
                const d = dist2(centre(x, y), p);
                if (d < bestD) {
                    bestD = d;
                    best = y * g.w + x;
                }
            }
        }
        if (best !== null)
            return best;
    }
    return null;
}
/** Breadth-first path of cell centres from a to b (8-way, no cutting corners), ending at b itself. */
export function findPath(map, a, b) {
    const g = gridOf(map);
    const from = nearestOpen(g, a);
    const to = nearestOpen(g, b);
    if (from === null || to === null)
        return [b];
    const prev = new Int32Array(g.w * g.h).fill(-1);
    prev[from] = from;
    const queue = [from];
    for (let i = 0; i < queue.length && prev[to] === -1; i++) {
        const c = queue[i];
        const x = c % g.w;
        const y = (c - x) / g.w;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                if (!dx && !dy)
                    continue;
                const nx = x + dx;
                const ny = y + dy;
                if (nx < 0 || ny < 0 || nx >= g.w || ny >= g.h)
                    continue;
                const n = ny * g.w + nx;
                if (!g.open[n] || prev[n] !== -1)
                    continue;
                if (dx && dy && (!g.open[y * g.w + nx] || !g.open[ny * g.w + x]))
                    continue;
                prev[n] = c;
                queue.push(n);
            }
        }
    }
    if (prev[to] === -1)
        return [b];
    const cells = [];
    for (let c = to; c !== from; c = prev[c])
        cells.push(centre(c % g.w, Math.floor(c / g.w)));
    cells.reverse();
    cells.push(b);
    return cells;
}
/** Can a keke drive from a to b at all (one open region of the path grid)? */
export function connected(map, a, b) {
    const g = gridOf(map);
    const from = nearestOpen(g, a);
    const to = nearestOpen(g, b);
    if (from === null || to === null)
        return false;
    if (from === to)
        return true;
    const seen = new Uint8Array(g.w * g.h);
    seen[from] = 1;
    const queue = [from];
    for (let i = 0; i < queue.length; i++) {
        const c = queue[i];
        const x = c % g.w;
        const y = (c - x) / g.w;
        for (const [dx, dy] of [
            [1, 0],
            [-1, 0],
            [0, 1],
            [0, -1],
        ]) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= g.w || ny >= g.h)
                continue;
            const n = ny * g.w + nx;
            if (!g.open[n] || seen[n])
                continue;
            if (n === to)
                return true;
            seen[n] = 1;
            queue.push(n);
        }
    }
    return false;
}
/**
 * A distance field over the path grid to one target cell (breadth-first, 8-way, no cutting
 * corners): steps from each open cell to the target, -1 where it can't be reached. Cached per map
 * and target cell (a few at a time), so a driver on foot can ask for the way every tick.
 */
const fields = new Map();
const FIELDS_KEPT = 24;
export function fieldTo(map, g, to) {
    const key = `${map.id}:${to}`;
    const have = fields.get(key);
    if (have)
        return have;
    const d = new Int32Array(g.w * g.h).fill(-1);
    d[to] = 0;
    const queue = [to];
    for (let i = 0; i < queue.length; i++) {
        const c = queue[i];
        const x = c % g.w;
        const y = (c - x) / g.w;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                if (!dx && !dy)
                    continue;
                const nx = x + dx;
                const ny = y + dy;
                if (nx < 0 || ny < 0 || nx >= g.w || ny >= g.h)
                    continue;
                const n = ny * g.w + nx;
                if (!g.open[n] || d[n] !== -1)
                    continue;
                if (dx && dy && (!g.open[y * g.w + nx] || !g.open[ny * g.w + x]))
                    continue;
                d[n] = d[c] + 1;
                queue.push(n);
            }
        }
    }
    if (fields.size >= FIELDS_KEPT)
        fields.delete(fields.keys().next().value);
    fields.set(key, d);
    return d;
}
/**
 * Where to head next on the way from `a` to `b` round the walls: `ahead` cells down the field (the
 * centre of that cell), or `b` itself once it is that close. Pure: the same answer on every machine.
 */
export function wayToward(map, a, b, ahead = 3) {
    const g = gridOf(map);
    const from = nearestOpen(g, a);
    const to = nearestOpen(g, b);
    if (from === null || to === null)
        return b;
    const d = fieldTo(map, g, to);
    if (d[from] === -1 || d[from] <= ahead)
        return b;
    let c = from;
    for (let k = 0; k < ahead; k++) {
        const x = c % g.w;
        const y = (c - x) / g.w;
        let next = c;
        for (let dy = -1; dy <= 1; dy++) {
            for (let dx = -1; dx <= 1; dx++) {
                const nx = x + dx;
                const ny = y + dy;
                if ((!dx && !dy) || nx < 0 || ny < 0 || nx >= g.w || ny >= g.h)
                    continue;
                const n = ny * g.w + nx;
                if (d[n] === -1 || d[n] >= d[next])
                    continue;
                if (dx && dy && (!g.open[y * g.w + nx] || !g.open[ny * g.w + x]))
                    continue;
                next = n;
            }
        }
        if (next === c)
            break;
        c = next;
    }
    return centre(c % g.w, Math.floor(c / g.w));
}
//# sourceMappingURL=path.js.map